import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { getSubscription, startUpgrade, cancelSubscription } from '../services/subscriptionService'
import type { DbSubscription, PlanTier } from '../types'

export function useSubscription(userId: string | null | undefined, householdId?: string | null) {
  const [subscription, setSubscription] = useState<DbSubscription | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!userId) return
    setLoading(true)
    setError(null)
    try {
      setSubscription(await getSubscription(userId, householdId))
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }, [userId, householdId])

  useEffect(() => { refresh() }, [refresh])

  // Realtime: listen for changes on the user's own subscription row
  useEffect(() => {
    if (!userId) return

    const channel = supabase
      .channel(`subscriptions:${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
          filter: `user_id=eq.${userId}`,
        },
        () => { refresh() },
      )
      .subscribe()

    return () => { supabase.removeChannel(channel) }
  }, [userId, refresh])

  // Realtime: listen for changes on the household's subscription (partner's plan)
  useEffect(() => {
    if (!householdId) return

    const channel = supabase
      .channel(`subscriptions:household:${householdId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
          filter: `household_id=eq.${householdId}`,
        },
        () => { refresh() },
      )
      .subscribe()

    return () => { supabase.removeChannel(channel) }
  }, [householdId, refresh])

  const planTier: PlanTier =
    subscription?.status === 'authorized' ? subscription.plan_tier : 'free'

  const isActive = subscription?.status === 'authorized'

  async function upgrade(
    planTier: Exclude<PlanTier, 'free'>,
    billingCycle: 'monthly' | 'annual',
    householdId: string | null,
    payerEmail: string,
  ): Promise<void> {
    if (!userId) return
    await startUpgrade({ userId, householdId, planTier, billingCycle, payerEmail })
  }

  async function cancel(): Promise<void> {
    if (!userId || !subscription) return
    await cancelSubscription(subscription.id, userId)
    await refresh()
  }

  return { subscription, planTier, isActive, loading, error, upgrade, cancel, refresh }
}
